import React, { useContext, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { SERVICE_SLUG_TO_CATEGORY, SERVICE_SLUG_TO_SUBCATEGORY } from "constants/categories";
import { Category } from "types/category";
import { IntlContext } from "context";
import Seo from "components/Seo";
import Support from "../../components/Support/Support";
import { ROUTES } from "routes";

import {
    SubCategoryAdsContent,
    SubCategoryFaqSection,
    SubCategoryHero,
    SubCategoryIntro,
    SubCategoryProvidersContent,
    SubCategorySection,
} from "./components";
import { formatOptional, subcategoryBase } from "./subcategoryIntl";
import type { SubCategoryCategoryKey, SubCategoryFaqItem } from "./types";

const SubCategoryPage: React.FC = () => {
    const { sub_category_label } = useParams() as {
        sub_category_label: string;
    };
    const intl = useContext(IntlContext);
    const navigate = useNavigate();

    const categoryKeyEnum = SERVICE_SLUG_TO_CATEGORY[sub_category_label];
    const subcategoryEnum = SERVICE_SLUG_TO_SUBCATEGORY[sub_category_label];
    const categoryGroup = categoryKeyEnum ? Category[categoryKeyEnum as keyof typeof Category] : undefined;

    const subFound = Boolean(
        categoryGroup && subcategoryEnum && categoryGroup.values.some((value) => value === subcategoryEnum),
    );
    const categoryKey = (categoryKeyEnum ?? "").toLowerCase() as SubCategoryCategoryKey;
    const subcategoryKey = (subcategoryEnum ?? "").toLowerCase();

    const { formatMessage, hasMessage } = intl;
    const base = subcategoryBase(categoryKey, subcategoryKey);

    const faqTitle = useMemo(() => {
        const title =
            formatOptional(hasMessage, formatMessage, `${base}.title`) ?? formatMessage(`${base}.description_short`);
        return formatOptional(hasMessage, formatMessage, `${base}.faq_title`) ?? title;
    }, [base, formatMessage, hasMessage]);

    const faqItems = useMemo(
        (): readonly SubCategoryFaqItem[] =>
            [1, 2, 3].map((index) => ({
                question: formatMessage(`${base}.quest${index}`),
                answer: formatMessage(`${base}.rep${index}`),
            })),
        [base, formatMessage],
    );

    const providersHref = `${ROUTES.PROVIDERS}?filter_subcategories=${subcategoryEnum}&order=rating&direction=desc`;
    const adsHref = `${ROUTES.ADS}?filter_subcategories=${subcategoryEnum}`;

    useEffect(() => {
        if (!subFound) navigate(ROUTES.HOME);
    }, [subFound, navigate]);

    const providersFetcherParams = useMemo(
        () => ({
            order: "rating",
            direction: "desc",
            filter_subcategories: subcategoryEnum,
            per_page: 6,
        }),
        [subcategoryEnum],
    );

    const adsFetcherParams = useMemo(
        () => ({
            order: "created_at",
            direction: "desc",
            filter_subcategories: subcategoryEnum,
            per_page: 6,
        }),
        [subcategoryEnum],
    );

    if (!subFound) return null;

    const noResultsLabel = intl.formatMessage("our_services.list.label.no_provider");
    const subcategoryLabel = subcategoryEnum
        ? String(
              intl.formatMessage(
                  `subcategory.${categoryKeyEnum.toLocaleLowerCase()}.${subcategoryEnum.toLocaleLowerCase()}.label`,
              ),
          )
        : "";
    const seoTitle = intl.formatMessage("subcategory.seo.title", { subcategory: subcategoryLabel });
    const seoDescription = intl.formatMessage("subcategory.seo.description", { subcategory: subcategoryLabel });

    return (
        <>
            <Seo title={seoTitle} description={seoDescription} ogType="website" />

            <div className="mb-10">
                <SubCategoryHero
                    categoryKey={categoryKey}
                    subcategoryKey={subcategoryKey}
                    providersHref={providersHref}
                />
                <SubCategoryIntro categoryKey={categoryKey} subcategoryKey={subcategoryKey} />

                <SubCategorySection
                    variant="providers"
                    viewAllHref={providersHref}
                    viewAllLabel={intl.formatMessage("subcategoryPage.providers_view_all")}
                >
                    <SubCategoryProvidersContent
                        queryKey={`subcategory-providers-${subcategoryEnum}`}
                        fetcherParams={providersFetcherParams}
                        title={intl.formatMessage("subcategoryPage.providers_title")}
                        categoryLabel={subcategoryLabel}
                        noResultsLabel={noResultsLabel}
                    />
                </SubCategorySection>

                <SubCategorySection
                    variant="ads"
                    viewAllHref={adsHref}
                    viewAllLabel={intl.formatMessage("subcategoryPage.ads_view_all")}
                >
                    <SubCategoryAdsContent
                        queryKey={`subcategory-ads-${subcategoryEnum}`}
                        fetcherParams={adsFetcherParams}
                        title={intl.formatMessage("subcategoryPage.ads_title")}
                        subcategory={subcategoryEnum}
                        subcategoryLabel={subcategoryLabel}
                        noResultsLabel={intl.formatMessage("ad.list.empty_title")}
                    />
                </SubCategorySection>

                <SubCategoryFaqSection
                    titleHighlight={faqTitle}
                    items={faqItems}
                    faqTitleSuffix={intl.formatMessage("subcategoryPage.faq_title_suffix")}
                />

                <Support className="mx-auto w-full max-w-[1400px] px-4 md:px-10" />
            </div>
        </>
    );
};

export default SubCategoryPage;
